import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { useBoolean } from 'ahooks';
import { Dropdown } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId } from 'api/models';
import { ProjectSource } from 'api/project';
import { FieldOption, StatusGroup } from 'api/tasks';
import { ReactComponent as FormInstaceIcon } from 'assets/svgs/form-instance-icon.svg';
import { ReactComponent as TaskTemplateIcon } from 'assets/svgs/save-task-template.svg';
import { Button } from 'components/atoms/button';
import { PermissionRuleName } from 'constant';
import { useIconTypeContext } from 'contexts/icon-type';
import { useProjectPhaseStatusField } from 'data/fields/hooks/account-fields';
import { ProjectPlanFilter, useProjectPhaseStore } from 'data/projects';
import { Task } from 'data/tasks';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useUpdateProjectPhaseMutation } from 'queries/project-phase';
import { AddFormPopover } from './add-form-popover';
import { CreateTaskPopover } from './create-task-popover';
import { TaskTemplateDrawer } from './drawer';
import { SwimlaneHeader } from './header';
import { PhaseStatusSummary } from './phase-status-summary';
import { Sortable } from './sortable';
import {
  CreatePopoverItem,
  List,
  PhaseStatusProgressBar,
  SwimlaneButtonWrapper,
  SwimlaneDropdownMenu,
  SwimlaneDropdownMenuItem,
  SwimlaneWrapper,
} from './swimlane.style';

interface SwimlaneProps {
  filter: ProjectPlanFilter;
  phaseId: number;
  projectId: number;
  isFullScreen?: boolean;
  showPrivateTasks?: boolean;
  projectPhaseIds: number[];
  sourceTemplates: ProjectSource[] | undefined;
  setSwimlaneState?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  swimlaneState?: Record<number, boolean>;
  isClose: boolean;
}

export const Swimlane: FC<SwimlaneProps> = ({
  filter,
  phaseId,
  projectId,
  projectPhaseIds,
  isFullScreen,
  showPrivateTasks,
  setSwimlaneState,
  swimlaneState,
  isClose,
  sourceTemplates,
}) => {
  const { mutate } = useUpdateProjectPhaseMutation(ProjectId(projectId));
  const [showTaskPopover, toggleShowTaskPopover] = useState(false);
  const [
    showTaskTemplateDrawer,
    { setTrue: showTemplateDrawer, setFalse: hideTemplateDrawer },
  ] = useBoolean(false);
  const [showCreateFormPopover, toggleShowCreateFormPopover] = useState(false);
  const [taskFormCreationInProgress, setTaskFormCreationInProgress] =
    useState(false);
  const [isMenuItemsShown, setIsMenuItemsShown] = useState(false);
  const { isExportUI } = useIconTypeContext();
  const taskFilterFunction = useCallback(
    (task: Task) => {
      return (
        filter.primary.filter(task, filter.primary) &&
        filter.secondary.filter(task, filter.secondary) &&
        (isFullScreen || !showPrivateTasks ? !task.private : true)
      );
    },
    [filter.primary, filter.secondary, isFullScreen, showPrivateTasks],
  );
  const {
    projectPhase,
    tasks: firstLevelTasks,
    completedTaskCount,
    inprogressTaskCount,
    subTaskCounter,
    allTasks,
    firstLevelTaskCounter,
  } = useProjectPhaseStore(projectId, phaseId, taskFilterFunction);

  const { isAllowedForCurrentUser } = usePermissionForRule();
  const statusField = useProjectPhaseStatusField();

  // TODO:: @abhilashlr
  if (!projectPhase) {
    return <></>;
  }

  const status = projectPhase.fields.find(
    (f) => f.fieldId === statusField.fieldId,
  );

  const onProjectPhaseChange = (_status: FieldOption<StatusGroup>) => {
    if (status) {
      mutate({
        projectPhaseId: phaseId,
        fields: [{ ...status, fieldValue: _status.value }],
      });
    }
  };
  const refId = `project-phase-${projectPhase.projectPhaseId}`;
  const phaseDatacy = `project-phase-${projectPhase.projectPhaseName}`;

  return (
    <SwimlaneWrapper
      data-cy={phaseDatacy}
      id={refId}
      private={projectPhase.private}
      className="swimlane"
      isClose={isClose}
    >
      <SwimlaneHeader
        projectPhase={projectPhase}
        projectId={ProjectId(projectId)}
        title={projectPhase.projectPhaseName}
        status={status?.fieldValue as number}
        onProjectPhaseChange={onProjectPhaseChange}
        setSwimlaneState={setSwimlaneState}
        swimlaneState={swimlaneState}
      />
      <PhaseStatusProgressBar
        startDate={projectPhase.startDate}
        dueDate={projectPhase.dueDate}
        total={allTasks.length}
        complete={completedTaskCount}
        progress={inprogressTaskCount}
        subTasks={subTaskCounter}
        firstLevelTaskCounter={firstLevelTaskCounter}
      />
      <List
        itemLayout="vertical"
        split={false}
        private={projectPhase.private}
        forExport={isExportUI}
      >
        {firstLevelTasks.length > 0 ? (
          <Sortable
            projectId={projectId}
            phaseId={projectPhase.projectPhaseId}
            itemsOrder={projectPhase.taskOrder}
            items={firstLevelTasks}
            isDisabled={
              isFullScreen ||
              !isAllowedForCurrentUser(
                PermissionRuleName.ProjectPhaseRearrangeTasksAction,
              )
            }
            taskFilterFunction={taskFilterFunction}
          />
        ) : null}

        <PhaseStatusSummary
          currentPhaseId={phaseId}
          projectPhaseIds={projectPhaseIds}
          status={status?.fieldValue as number}
        />

        {!isFullScreen &&
          isAllowedForCurrentUser(
            PermissionRuleName.ProjectPhaseAddTasksAction,
          ) && (
            <CreatePopoverItem
              visible={
                showTaskPopover ||
                showCreateFormPopover ||
                taskFormCreationInProgress ||
                isMenuItemsShown
              }
            >
              <SwimlaneButtonWrapper>
                <Button
                  size="small"
                  type="primary"
                  loading={taskFormCreationInProgress}
                  onClick={() => toggleShowTaskPopover(true)}
                  data-cy={`${projectPhase.projectPhaseName}.add-task-button`}
                >
                  <FormattedMessage id="toolbar.add_task" />
                </Button>
                <Dropdown
                  placement="bottomRight"
                  onVisibleChange={(value) => {
                    setIsMenuItemsShown(value);
                  }}
                  disabled={taskFormCreationInProgress}
                  overlay={
                    <SwimlaneDropdownMenu
                      onClick={() => {
                        setIsMenuItemsShown(false);
                      }}
                    >
                      <SwimlaneDropdownMenuItem
                        key="1"
                        onClick={() => toggleShowCreateFormPopover(true)}
                      >
                        <FormInstaceIcon />
                        <FormattedMessage id="forms.response.add_form" />
                      </SwimlaneDropdownMenuItem>
                      <SwimlaneDropdownMenuItem
                        key="2"
                        onClick={showTemplateDrawer}
                      >
                        <TaskTemplateIcon />
                        <FormattedMessage id="toolbar.add_from_task_template" />
                      </SwimlaneDropdownMenuItem>
                    </SwimlaneDropdownMenu>
                  }
                >
                  <Button
                    type="primary"
                    size="small"
                    icon={<ChevronDown16 />}
                    data-cy={`${projectPhase.projectPhaseName}.drop-down-button`}
                  />
                </Dropdown>
                <AddFormPopover
                  visible={showCreateFormPopover}
                  toggle={toggleShowCreateFormPopover}
                  phaseId={projectPhase.projectPhaseId}
                  projectId={projectId}
                  setTaskFormCreationInProgress={setTaskFormCreationInProgress}
                />
                <CreateTaskPopover
                  visible={showTaskPopover}
                  toggle={toggleShowTaskPopover}
                  phaseId={projectPhase.projectPhaseId}
                  projectId={projectId}
                  isPrivate={projectPhase.private}
                />
                <TaskTemplateDrawer
                  visible={showTaskTemplateDrawer}
                  onClose={hideTemplateDrawer}
                  sourceTemplates={sourceTemplates}
                  projectId={projectId}
                  phaseId={projectPhase.projectPhaseId}
                  phaseName={projectPhase.projectPhaseName}
                />
              </SwimlaneButtonWrapper>
            </CreatePopoverItem>
          )}
      </List>
    </SwimlaneWrapper>
  );
};
